import { createContext, useContext } from "react";

/**
 * Issue #797 fix round (review, B1). Lets the RENDERED SCREEN declare "I am
 * a rebuilt screen whose text-size control is retired" directly, rather
 * than `Layout.jsx` guessing it from the route and a build-time flag alone.
 *
 * WHY A ROUTE ALONE ISN'T ENOUGH. `hidesTextSizeControl(pathname)`
 * (`lib/chromelessRoutes.js`) is still correct for a screen where route +
 * flag IS the whole truth — `/onboarding` and `/coach-setup` each have
 * exactly one composition behind their own flag. `/coach-portal` does not:
 * the SAME route serves the shipped, populated player list (control must
 * stay — #797's own text, "old screens... must keep doing so until they
 * are replaced") and the redrawn empty state (control retired), and which
 * one is showing depends on `CoachPortal.jsx`'s own `rosterLoaded &&
 * players.length === 0` — data `Layout.jsx` cannot see; it only wraps
 * `<Outlet/>`. The review's own reproduction: a demo coach with players,
 * flag on, still reached the redrawn-only "Aa" trigger, because the old
 * route-level check could not tell the two compositions apart.
 *
 * THE DEFAULT VALUE'S SETTER IS A NO-OP. A screen that renders outside
 * `<Layout/>` entirely (`SignUpNew`/`Register` on `/register`) never sees a
 * `RetireTextSizeProvider` above it at all, and a screen that never calls
 * `useRetireTextSize()` costs nothing — this context is opt-in per screen,
 * not a second flag system every page must wire into.
 */
const RetireTextSizeContext = createContext({ retired: false, setRetired: () => {} });

/** The rendered screen's own read/write handle — call `setRetired(true)` on
 * mount and `setRetired(false)` on unmount (a `useEffect` cleanup), never
 * conditionally re-call it on every render: `Layout.jsx` holds this as
 * ordinary `useState`, so two screens both leaving it `true` on unmount
 * would fight each other, and the effect shape is what keeps exactly the
 * ONE currently-mounted screen owning the value. */
export function useRetireTextSize() {
  return useContext(RetireTextSizeContext);
}

export const RetireTextSizeProvider = RetireTextSizeContext.Provider;
